<script lang="ts">
import { defineComponent } from 'vue';
import { ApplicationHelper } from "../../../helpers/ApplicationHelper";
import Icon from "../../components/Icon.vue";

export default defineComponent({
  name: "DonationCard",
  components: { Icon },

  props: {
    donation: { type: Object, required: true },
    clickable: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    altRow: { type: Boolean, default: false }
  },

  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
      },

    classes(){
      return {
        'donation-card': true,
        'donation-card--clickable': this.clickable,
        'donation-card--alt': this.altRow,
        'active': this.active
      };
    },

    target(){
      return this.donation?.donatable || {};
    },

    targetName(){
      return this.target?.name || '—';
    },

    ///banner_image_url logo in the data card comes from the donatable(campaign/fund and not the organization
    logoUrl(){
      return this.target?.banner_image_url || this.target?.logo_url || this.donation?.organization?.logo_url || null;
    },

    orgInitial(){
      return (this.donation?.organization_name?.trim()?.charAt(0) || "•").toUpperCase();
    },

    isRecurring(){
      return this.donation?.subscription;
    },
  },
});
</script>

<template>
  <div :class="classes">
    <!-- Left section -->
    <div class="flex flex-col flex-1 min-w-0">
      <!-- Avatar + Organization name -->
      <div class="flex flex-row items-center">
        <span class="inline-flex items-center justify-center avatar mr-2">
          <img
            v-if="logoUrl"
            :src="logoUrl"
            alt="Logo"
            class="w-5 h-5 rounded-full object-cover"
          >
          <span
            v-else
            class="avatar-fallback"
          >{{ orgInitial }}</span>
        </span>

        <div class="font-medium text-sm text-gray-900">
          {{ targetName }}
        </div>

        <div
          v-if="isRecurring"
          class="subscription-pill pill ml-2 recurring-desktop"
        >
          Recurring
        </div>
      </div>

      <!-- Fund/Campaign name + Paid date + Next date inline -->
      <div class="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-1 items-baseline">
        <span>
          {{ ApplicationHelper.formatDate(donation.paid_at, { time: false, weekday: false }) }}
        </span>
      </div>
    </div>

    <!-- Right section -->
    <div class="flex flex-row items-center flex-shrink-0">
      <!-- Recurring (mobile only) -->
      <div
        v-if="isRecurring"
        class="subscription-pill pill mr-2 recurring-mobile"
      >
        Recurring
      </div>

      <!-- Amount -->
      <div class="donation-amount-pill">
        {{ ApplicationHelper.formatMoney(donation.amount, true) }}
      </div>

      <div
        v-if="clickable"
        class="ml-6"
      >
        <icon
          icon="chevron-right"
          size="10px"
        />
      </div>
    </div>
  </div>
</template>



<style scoped lang="scss">
.pill {
  @apply rounded-full px-2 py-0.5 text-[10px] font-medium;
}
.subscription-pill {
  @apply text-[9px] font-semibold px-2 py-0.5 rounded-full leading-tight;
  color: var(--brand-primary-text, #ffffff);
  background: var(--brand-primary, #10be5a);
  border: var(--brand-primary-border, 1px solid transparent);
}

.donation-card {
  @apply flex items-center justify-between px-3 py-3 bg-white rounded-md;
  @apply border border-gray-200 text-base;

  &.donation-card--alt {
    background: color-mix(in srgb, var(--brand-primary, #10be5a) 5%, #fff);
  }

  &.donation-card--alt.donation-card--clickable:hover {
    background: color-mix(in srgb, var(--brand-primary, #10be5a) 7%, #fff);
  }

  &.donation-card--clickable {
    cursor: pointer;
    &:hover { @apply bg-gray-50; }
    &.active { @apply bg-gray-100; }
  }

  .donation-amount-pill {
    @apply font-semibold text-xs px-3 py-1 rounded-md whitespace-nowrap;
    color: var(--brand-primary-text, #ffffff);
    background: var(--brand-primary, #10be5a);
    border: var(--brand-primary-border, 1px solid transparent);
  }
}

/* Avatar + fallback */
.avatar {
  @apply w-5 h-5 rounded-full overflow-hidden bg-gray-200 flex-shrink-0;
}
.avatar-fallback {
  @apply w-full h-full flex items-center justify-center text-[10px] font-semibold text-gray-700;
}

.recurring-mobile {
  display: none !important;
}

.recurring-desktop {
  display: inline-flex !important;
}

@media (max-width: 640px) {
  .recurring-mobile {
    display: inline-flex !important;
  }
  .recurring-desktop {
    display: none !important;
  }
}
</style>
